import { useQuery } from "@tanstack/react-query";
import httpClient from "../httpClient";
import { getCorrelationId } from "../../utils";

// The served shape is PaymentStatus; `data` is the legacy nesting the status
// page has always tolerated and is kept so a change of contract cannot blank
// the screen. `error` is InstrumentError -- left unknown here because the
// resolver, not this hook, decides which of its fields a payer may see.
export type PaymentStatusEnvelope = {
  status?: string;
  error?: unknown;
  // The business reference a payer quotes, served IN the body -- unlike the
  // minted `reference` beside the envelope. Absent means the key is missing.
  payment_reference?: string;
  // What the server recorded as the amount of this payment.
  amount?: number | string;
  data?: { status?: string; error?: unknown };
};

// The reference travels BESIDE the envelope rather than spread into it, so a
// `reference` field arriving from the server can never be shadowed by the one
// minted here.
export type PaymentStatusResult = {
  payment?: PaymentStatusEnvelope;
  reference: string;
};

type UseQueryPaymentStatusArgs = {
  paymentItemId: string;
  enabled?: boolean;
  authorisation: string;
};

const hasFinalStatus = (result?: PaymentStatusResult) => {
  const raw = (result?.payment?.status || result?.payment?.data?.status || "")
    .toString()
    .toUpperCase();

  return ["SUCCESS", "PAID", "FAILED", "ERROR", "DECLINED"].includes(raw);
};

export function useQueryPaymentStatus({
  paymentItemId,
  enabled = true,
  authorisation,
}: UseQueryPaymentStatusArgs) {
  return useQuery<PaymentStatusResult>(
    ["paymentStatus", paymentItemId],
    async () => {
      // Minted at the call site, as useSchoolFeePayMutation already does: a
      // FAILED payment arrives on a 200, so the seam's error path never sees
      // it and this is the only way the caller holds the value the request
      // sent (D-17).
      const reference = getCorrelationId();

      const payment = await httpClient(`payment/pay/${paymentItemId}`, {
        authorisation,
        correlationId: reference,
        headers: { Accept: "application/json" },
      }).get<PaymentStatusEnvelope | undefined>();

      return { payment, reference };
    },
    {
      enabled: enabled && !!paymentItemId,
      // A status read failing does not mean the payment failed. Keep polling
      // when there is no result (or an unrecognised, non-terminal result) and
      // stop only when the payment service reports a final business status.
      refetchInterval: (result?: PaymentStatusResult) =>
        hasFinalStatus(result) ? false : 3000,

      retry: 1,
    }
  );
}
